import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { catchError, map, Observable, of, OperatorFunction, Subscription, switchMap, tap, throwError } from 'rxjs';
import { Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { DEFAULT_RETRY_BACKOFF_TIME, retryWithBackoff, STATUS_CODES_FOR_RETRY } from '@fcom/rx';
import { mapErrorForMonitoring, unsubscribe } from '@fcom/core';
import { CheckInAcceptCancelRequest } from '@fcom/dapi/api/models/check-in-accept-cancel-request';
import { CheckInBoardingPassByEmailRequest } from '@fcom/dapi/api/models/check-in-boarding-pass-by-email-request';
import { CheckInBoardingPassByEmailResponse } from '@fcom/dapi/api/models/check-in-boarding-pass-by-email-response';
import { CheckInInitializeRequest } from '@fcom/dapi/api/models/check-in-initialize-request';
import { CheckInReassignExitSeatsRequest } from '@fcom/dapi/api/models/check-in-reassign-exit-seats-request';
import { FinnairAdvancePassengerInformationRequest } from '@fcom/dapi/api/models/finnair-advance-passenger-information-request';
import { FinnairAdvancePassengerInformationRequestV2 } from '@fcom/dapi/api/models/finnair-advance-passenger-information-request-v-2';
import { FinnairEmergencyContactUpdateRequest } from '@fcom/dapi/api/models/finnair-emergency-contact-update-request';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerRequest } from '@fcom/dapi/api/models/finnair-passenger-request';
import { FinnairServiceRequestItem } from '@fcom/dapi/api/models/finnair-service-request-item';
import { RequestInitiator } from '@fcom/dapi/api/models/request-initiator';
import { TourOperatorCode } from '@fcom/dapi/api/models/tour-operator-code';
import { isNull } from '@fcom/core/utils/utils';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';
import { getDapiErrorKey } from '@fcom/common-booking/utils/common-booking.utils';
import { DapiService } from '@fcom/dapi/api/dapi.service';

import { acceptCheckIn } from '../../dapi/api/fn/checkin/accept-check-in';
import { cancelCheckIn } from '../../dapi/api/fn/checkin/cancel-check-in';
import { fetchOrder } from '../../dapi/api/fn/order/fetch-order';
import { getOrderByPnrAndLastName } from '../../dapi/api/fn/order/get-order-by-pnr-and-last-name';
import { getOrderByTourOperatorAndLastName } from '../../dapi/api/fn/order/get-order-by-tour-operator-and-last-name';
import { initializeCheckIn } from '../../dapi/api/fn/checkin/initialize-check-in';
import { reassignExitSeats } from '../../dapi/api/fn/checkin/reassign-exit-seats';
import { sendBoardingPass } from '../../dapi/api/fn/checkin/send-boarding-pass';
import { setServices } from '../../dapi/api/fn/order/set-services';
import { updateAdvancePassengerInformation } from '../../dapi/api/fn/advance-passenger-information/update-advance-passenger-information';
import { updateApis } from '../../dapi/api/fn/advance-passenger-information/update-apis';
import { updateEmergencyContactDetails } from '../../dapi/api/fn/emergency-contact/update-emergency-contact-details';
import { updatePassengerDetails } from '../../dapi/api/fn/order/update-passenger-details';
import { loadUpgradeOffers } from '../upgrade-offers.actions';

const ORDER_STATUS_CODES_FOR_RETRY = [503, ...STATUS_CODES_FOR_RETRY];

@Injectable({ providedIn: 'root' })
export class MmbOrderService implements OnDestroy {
  private http = inject(HttpClient);
  private dapiService = inject(DapiService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);
  private store = inject(Store);

  static NUMBER_OF_RETRIES = 1;
  static readonly LOCATION_HEADER = 'Location';

  private subscriptions = new Subscription();

  private readonly CATCH_ERROR: (
    isTourOperator?: boolean,
    trackingCallback?: (errorKey: string) => void
  ) => OperatorFunction<unknown, unknown> = (isTourOperator = false) =>
    catchError((err: unknown) => {
      if (!getDapiErrorKey(err).startsWith('ORDER_')) {
        this.logErrorToSentry(err, getDapiErrorKey(err), isTourOperator);
      }

      return throwError(() => {
        return err;
      });
    });

  logErrorToSentry(err: unknown, errorKey: string, isTourOperator = false): void {
    this.monitoringLogger.error(`Error fetching order ${isTourOperator ? 'for tour operator ' : ''}${errorKey}`, {
      error: mapErrorForMonitoring(err),
    });
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private locationFromResponse = (response: DapiHttpResponse<void>) =>
    response.headers.get(MmbOrderService.LOCATION_HEADER);

  /**
   * load Order by PNR and surname
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   * @param requestInitiator requestInitiator to define where we started the call
   * @param firstName to be used for group bookings
   */
  loadOrder(
    familyName: string,
    bookingRef: string,
    requestInitiator: RequestInitiator,
    firstName?: string,
    fetchUpgradeOffers = true
  ): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(getOrderByPnrAndLastName, this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          requestInitiator,
          ...(firstName ? { firstName } : {}),
        },
      })
      .pipe(
        this.retryGet$(),
        this.CATCH_ERROR() as OperatorFunction<string | null, string>,
        tap(() => {
          if (fetchUpgradeOffers) {
            this.store.dispatch(loadUpgradeOffers({ recLoc: bookingRef.toUpperCase(), lastName: familyName }));
          }
        }),
        switchMap((orderLocation: string) => this.getOrderByUrl(orderLocation))
      );
  }

  /**
   * load Order by booking reference, surname and tour operator code
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   * @param tourOperatorCode tourOperatorCode for the PNR to fetch
   */
  loadOrderTourOperator(
    familyName: string,
    bookingRef: string,
    tourOperatorCode: TourOperatorCode,
    fetchUpgradeOffers = true
  ): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(getOrderByTourOperatorAndLastName, this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          requestInitiator: RequestInitiator.MMB,
          tourOperatorCode,
        },
      })
      .pipe(
        this.retryGet$(),
        this.CATCH_ERROR(true) as OperatorFunction<string | null, string>,
        tap(() => {
          if (fetchUpgradeOffers) {
            this.store.dispatch(loadUpgradeOffers({ recLoc: bookingRef.toUpperCase(), lastName: familyName }));
          }
        }),
        switchMap((orderLocation: string) => this.getOrderByUrl(orderLocation))
      );
  }

  completeCheckIn(orderId: string, hash: string, body: CheckInAcceptCancelRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(acceptCheckIn, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  reassignExitRowSeats(orderId: string, hash: string, body: CheckInReassignExitSeatsRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(reassignExitSeats, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  sendBoardingPass(
    orderId: string,
    hash: string,
    body: CheckInBoardingPassByEmailRequest
  ): Observable<CheckInBoardingPassByEmailResponse> {
    return this.dapiService.invoke$(sendBoardingPass, this.configService.cfg.orderUrl, {
      orderId,
      hash,
      body,
    });
  }

  cancelCheckIn(orderId: string, hash: string, body: CheckInAcceptCancelRequest): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(cancelCheckIn, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        body,
      })
    );
  }

  setServices$(orderId: string, hash: string, serviceRequests: FinnairServiceRequestItem[]): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(setServices, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequests,
      })
    );
  }

  updateEmergencyContactDetails$(
    orderId: string,
    hash: string,
    serviceRequest: FinnairEmergencyContactUpdateRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateEmergencyContactDetails, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateApis(
    orderId: string,
    hash: string,
    serviceRequest: FinnairAdvancePassengerInformationRequest[]
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateAdvancePassengerInformation, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateRegulatoryDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairAdvancePassengerInformationRequestV2[]
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updateApis, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateContactDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairPassengerRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.dapiService.invokeResponse$(updatePassengerDetails, this.configService.cfg.orderUrl, {
        orderId,
        hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  initializeCheckIn(orderId: string, hash: string, request: CheckInInitializeRequest): void {
    this.subscriptions.add(
      this.dapiService
        .invokeResponse$(initializeCheckIn, this.configService.cfg.orderUrl, {
          orderId: orderId,
          hash: hash,
          body: request,
        })
        .subscribe({
          error: (err: unknown) =>
            this.monitoringLogger.error('Check-in initialize call failed with ' + getDapiErrorKey(err), {
              error: mapErrorForMonitoring(err),
            }),
        })
    );
  }

  fetchOrderAfterRequest(request$: Observable<DapiHttpResponse<void>>): Observable<FinnairOrder> {
    return request$.pipe(
      map((res) => this.locationFromResponse(res)),
      retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [503, ...STATUS_CODES_FOR_RETRY]),
      switchMap((location: string) => this.getOrderByUrl(location))
    );
  }

  getOrderByIdAndHash(orderId: string, hash: string): Observable<FinnairOrder> {
    return this.dapiService
      .invokeResponse$(fetchOrder, this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        includeServiceCatalog: true,
        ...(this.configService.cfg.enableAncillaryFrontendQuota ? { frontEndQuota: true } : {}),
      })
      .pipe(
        retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [
          503,
          ...STATUS_CODES_FOR_RETRY,
        ]),
        catchError((err: unknown) => {
          this.logErrorToSentry(err, getDapiErrorKey(err));
          return throwError(() => err);
        }),
        map((response: DapiHttpResponse<FinnairOrder>) => response.body as FinnairOrder)
      );
  }

  getOrderByUrl(orderUrl: string | null): Observable<FinnairOrder> {
    if (isNull(orderUrl)) {
      return of(null);
    }

    let params = new HttpParams().set('includeServiceCatalog', true);
    if (this.configService.cfg.enableAncillaryFrontendQuota) {
      params = params.set('frontEndQuota', true);
    }

    return this.http.get<FinnairOrder>(orderUrl, { params }).pipe(
      retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [503, ...STATUS_CODES_FOR_RETRY]),
      catchError((err: unknown) => {
        this.logErrorToSentry(err, getDapiErrorKey(err));
        return throwError(() => err);
      })
    );
  }

  private retryGet$() {
    return (o$: Observable<DapiHttpResponse<void>>) =>
      o$.pipe(
        map((res) => this.locationFromResponse(res)),
        retryWithBackoff(MmbOrderService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, ORDER_STATUS_CODES_FOR_RETRY)
      );
  }
}
